import type { PageArticleFieldsFragment } from "./__generated/page-article.contentful.generated";
import { hasCloudinaryAsset } from "@project/ui";
import { useReadingTimeFromRichText } from "@/lib/contentful/use-reading-time-from-rich-text";
import {
    type MicrocopyInputType,
    useMicrocopy,
} from "@/lib/contentful/microcopy/microcopy-context";
import { HeroSplitScreen } from "@/components/hero-layouts/hero-split-screen";
import { ArticleTitleDefault } from "./article-header-title-default";
import { HeroFullScreen } from "@/components/hero-layouts/hero-full-screen";
import { HeroFullScreenHeaderComponent } from "@/components/hero-layouts/hero-full-screen-header-component";
import { ArticleTopicDateComponent } from "./article-topic-date-component";
import { HeroTitleComponent } from "@/components/hero-layouts/hero-title-component";
import { ArticleReadingTimeComponent } from "./article-reading-time-component";
import { Tags } from "@/components/tags";
import { ArticleContent } from "@/components/article-content";

type PageArticleProps = PageArticleFieldsFragment & {
    microcopySets: MicrocopyInputType[];
};

export const PageArticle = (props: PageArticleProps) => {
    const { bodyText, modulesCollection, presentation, microcopySets, ...restProps } = props;

    const readingTime = useReadingTimeFromRichText(bodyText?.json, { wordsPerMinute: 200 });

    const { add: addMicrocopy } = useMicrocopy();
    if (microcopySets) {
        microcopySets.forEach((microcopySet) => addMicrocopy(microcopySet));
    }
    const isDefaultLayout =
        hasCloudinaryAsset(restProps.heroAsset) && presentation !== "full screen";

    return (
        <section>
            {isDefaultLayout ? (
                <HeroSplitScreen
                    {...restProps}
                    heroAsset={restProps.heroAsset}
                    primaryComponent={
                        <ArticleTitleDefault readingTime={readingTime} {...restProps} />
                    }
                />
            ) : (
                <HeroFullScreen
                    {...restProps}
                    heroAsset={restProps.heroAsset ?? null}
                    fullScreenHeaderComponent={
                        <HeroFullScreenHeaderComponent
                            overTitleComponent={<ArticleTopicDateComponent {...restProps} />}
                            gridItem1={
                                <HeroTitleComponent title={restProps.title} {...restProps} />
                            }
                            gridItem2={<ArticleReadingTimeComponent readingTime={readingTime} />}
                            gridItem3={<Tags tagsCollection={restProps?.tagsCollection?.items} />}
                        />
                    }
                />
            )}
            <ArticleContent {...props} />
        </section>
    );
};
